// =============================================================================
// SubscriptionManagement.jsx - Subscription & Billing Management
// =============================================================================
// Displays current subscription status, allows upgrading/downgrading plans,
// and manages billing information with Stripe integration
// Includes monthly/yearly billing period toggle with savings display
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { PLAN } from '../../config/stripe';
import { auth } from '../../utils/firebase';
import './SubscriptionManagement.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SubscriptionManagement = ({
  clubInfo,
  subscriptionTier = 'free',
  subscriptionStatus = 'trial',
  showSuccess,
  isAdmin = false
}) => {
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const location = useLocation();

  // ===========================================================================
  // EFFECTS
  // ===========================================================================
  // Reset all state when location changes (returning from Stripe, etc)
  useEffect(() => {
    console.log('📍 Location changed, resetting loading state');
    setLoading(false);
    setLoadingPlan(null);
    setError(null);
  }, [location]);

  // Also reset loading state when component unmounts and remounts
  useEffect(() => {
    return () => {
      setLoading(false);
      setLoadingPlan(null);
    };
  }, []);

  // Fallback: Reset loading state after 10 seconds if stuck
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        console.warn('⚠️ Loading state stuck for 10s, forcing reset');
        setLoading(false);
        setLoadingPlan(null);
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [loading]);

  // ===========================================================================
  // SUBSCRIPTION TIERS INFO
  // ===========================================================================
  const tierInfo = {
    free: {
      name: 'Free',
      monthlyPrice: 0,
      features: ['Read-only access', 'View allocations', 'Basic reporting'],
      color: '#6b7280'
    },
    pitchallo: {
      name: 'Pitchallo',
      color: '#667eea'
    }
  };

  const currentTierInfo = tierInfo[subscriptionTier] || tierInfo.free;

  // Get current pricing based on billing period
  const getCurrentPrice = () => {
    if (subscriptionTier === 'free') return 0;
    return billingPeriod === 'monthly' 
      ? PLAN.monthly.price 
      : PLAN.yearly.price;
  };

  const getCurrentPriceDisplay = () => {
    if (subscriptionTier === 'free') return 'Free';
    return billingPeriod === 'monthly'
      ? `$${PLAN.monthly.price}/month`
      : `$${PLAN.yearly.price}/year`;
  };

  // ===========================================================================
  // STATUS DISPLAY
  // ===========================================================================
  const getStatusDisplay = () => {
    switch (subscriptionStatus) {
      case 'trial':
        return {
          text: 'Trial Active',
          color: '#fde047',
          bgColor: 'rgba(234, 179, 8, 0.1)',
          icon: '⏱'
        };
      case 'active':
        return {
          text: 'Active',
          color: '#86efac',
          bgColor: 'rgba(34, 197, 94, 0.1)',
          icon: '✓'
        };
      case 'expired':
        return {
          text: 'Expired',
          color: '#fca5a5',
          bgColor: 'rgba(239, 68, 68, 0.1)',
          icon: '✕'
        };
      case 'canceled':
        return {
          text: 'Canceled',
          color: '#fca5a5',
          bgColor: 'rgba(239, 68, 68, 0.1)',
          icon: '⚠'
        };
      default:
        return {
          text: subscriptionStatus,
          color: '#9ca3af',
          bgColor: '#f3f4f6',
          icon: '○'
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  // ===========================================================================
  // TRIAL INFO
  // ===========================================================================
  const getTrialInfo = () => {
    if (subscriptionStatus !== 'trial' || !clubInfo?.trialEndsAt) return null;

    const now = new Date();
    const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
      clubInfo.trialEndsAt.toDate() : 
      new Date(clubInfo.trialEndsAt);
    
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

    if (daysRemaining <= 0) {
      return {
        expired: true,
        text: 'Trial expired',
        color: '#dc2626'
      };
    }

    return {
      expired: false,
      daysRemaining,
      text: `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`,
      color: daysRemaining <= 2 ? '#dc2626' : '#6b7280'
    };
  };

  const trialInfo = getTrialInfo();

  // ===========================================================================
  // HANDLERS: Upgrade/Downgrade with Stripe
  // ===========================================================================
  const handleUpgrade = async () => {
    if (!isAdmin) {
      setError('Only administrators can change subscription plans');
      return;
    }

    setLoading(true);
    setLoadingPlan('pitchallo');
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError('Please sign in to subscribe');
        setLoading(false);
        setLoadingPlan(null);
        return;
      }

      // Get price ID from PLAN based on billing period
      const priceId = billingPeriod === 'monthly' 
        ? PLAN.monthly.priceId 
        : PLAN.yearly.priceId;

      if (!priceId) {
        throw new Error(`No price ID found for ${billingPeriod} billing`);
      }

      console.log('Creating checkout session for:', {
        tier: 'pitchallo',
        billingPeriod: billingPeriod,
        priceId: priceId,
        clubId: clubInfo?.clubId,
        userId: user.uid
      });

      // Call Netlify function to create checkout session
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId,
          userId: user.uid,
          userEmail: user.email,
          clubId: clubInfo?.clubId,
          tier: 'pitchallo',
          billingPeriod: billingPeriod,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { sessionId } = await response.json();

      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionId,
      });

      if (error) {
        // If there's an error, reset loading state
        setLoading(false);
        setLoadingPlan(null);
        throw error;
      }
      // If redirectToCheckout succeeds, the page will navigate away
      // So we don't need to reset loading state here
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
      setLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!isAdmin) {
      setError('Only administrators can manage billing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError('Please sign in to manage subscription');
        setLoading(false);
        return;
      }

      console.log('Opening Stripe customer portal for club:', clubInfo?.clubId);

      // Call Netlify function to create portal session
      const response = await fetch('/.netlify/functions/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clubId: clubInfo?.clubId,
          userId: user.uid,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error opening customer portal:', err);
      setError(err.message || 'Failed to open billing portal. Please try again.');
      setLoading(false);
    }
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (
    <div className="subscription-management-wrapper">
      <div 
        className="subscription-management-header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
      >
        <div>
          <h2 className="subscription-management-title">Subscription & Billing</h2>
          <p className="subscription-management-subtitle">
            Everything you need to manage your account
          </p>
        </div>
        <span className={`subscription-management-chevron ${isExpanded ? 'expanded' : ''}`}>
          ▼
        </span>
      </div>

      {isExpanded && (
        <div className="subscription-management-content">
          {/* Current Subscription */}
          <div className="current-subscription-card">
            <div className="subscription-status-row">
              <h3>Current Subscription</h3>
              <div className="subscription-status-badge" style={{ color: statusDisplay.color, backgroundColor: statusDisplay.bgColor }}>
                {statusDisplay.icon} {statusDisplay.text}
              </div>
            </div>

            <div className="subscription-tier-name">{currentTierInfo.name}</div>
            <div className="subscription-tier-price">{getCurrentPriceDisplay()}</div>

            {/* Trial Info */}
            {trialInfo && (
              <div className={`subscription-notice ${trialInfo.expired ? 'expired' : 'active'}`}>
                <div style={{ fontSize: '13px', color: trialInfo.color, fontWeight: '500' }}>
                  {trialInfo.expired ? '⚠️ ' : '⏱ '}
                  {trialInfo.text}
                </div>
              </div>
            )}

            {/* Pending Tier Change Notice */}
            {clubInfo?.pendingTier && clubInfo?.currentPeriodEnd && (
              <div className="subscription-notice pending">
                <div style={{ fontSize: '13px', color: '#92400e', fontWeight: '500' }}>
                  📅 On {clubInfo.pendingTierRequestedAt?.toDate?.() 
                    ? clubInfo.pendingTierRequestedAt.toDate().toLocaleDateString() 
                    : new Date(clubInfo.pendingTierRequestedAt).toLocaleDateString()
                  } you decided to change your plan. 
                  This change will occur at the end of your billing cycle ({
                    clubInfo.currentPeriodEnd?.toDate?.() 
                      ? clubInfo.currentPeriodEnd.toDate().toLocaleDateString() 
                      : new Date(clubInfo.currentPeriodEnd).toLocaleDateString()
                  }).
                </div>
              </div>
            )}

            {/* Current Features */}
            {subscriptionTier !== 'free' && (
              <div className="subscription-features">
                <div className="subscription-features-title">Included Features:</div>
                <div className="subscription-features-list">
                  {[
                    'Unlimited pitches',
                    'Unlimited teams',
                    'Unlimited user accounts',
                    'Advanced scheduling',
                    'Weather alerts',
                    'Cloud backup',
                    'Smart fixture tool',
                    'Change log'
                  ].map((feature, idx) => (
                    <div key={idx} className="subscription-feature-item">
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Information */}
          {(subscriptionStatus === 'active' || subscriptionStatus === 'trial') && (
            <div className="payment-information">
              {/* Latest Payment */}
              <div className="payment-section">
                <h3>Latest Payment Made</h3>
                {clubInfo?.lastPayment ? (
                  <div className="payment-table">
                    <div className="payment-row payment-header">
                      <div className="payment-cell">Plan</div>
                      <div className="payment-cell">Amount</div>
                      <div className="payment-cell">Date</div>
                    </div>
                    <div className="payment-row">
                      <div className="payment-cell">{tierInfo[subscriptionTier]?.name || subscriptionTier}</div>
                      <div className="payment-cell">
                        ${clubInfo.lastPayment.amount ? (clubInfo.lastPayment.amount / 100).toFixed(2) : getCurrentPrice()}
                      </div>
                      <div className="payment-cell">
                        {clubInfo.lastPayment.date?.toDate?.() 
                          ? clubInfo.lastPayment.date.toDate().toLocaleDateString() 
                          : new Date(clubInfo.lastPayment.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="payment-placeholder">
                    <p>No payments recorded yet. Your first payment will appear here.</p>
                  </div>
                )}
              </div>

              {/* Next Payment Due */}
              <div className="payment-section">
                <h3>Next Payment Due</h3>
                {clubInfo?.currentPeriodEnd ? (
                  <div className="next-payment">
                    <span className="next-payment-label">Scheduled for:</span>
                    <span className="next-payment-date">
                      {clubInfo.currentPeriodEnd?.toDate?.() 
                        ? clubInfo.currentPeriodEnd.toDate().toLocaleDateString() 
                        : new Date(clubInfo.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  </div>
                ) : (
                  <div className="payment-placeholder">
                    <p>Billing cycle information will appear here.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="subscription-error">
              {error}
            </div>
          )}

          {/* Available Plan */}
          {(subscriptionStatus === 'trial' || subscriptionStatus === 'expired') && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '16px', marginTop: '20px' }}>
                Subscribe Now
              </h3>

              {/* Billing Period Toggle */}
              <div className="billing-toggle-wrapper">
                <span className="toggle-label">Bill Monthly</span>
                <button
                  onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                  className={`toggle-switch ${billingPeriod}`}
                  aria-label={`Switch to ${billingPeriod === 'monthly' ? 'yearly' : 'monthly'} billing`}
                />
                <span className="toggle-label">Bill Annually</span>
              </div>

              <div className="subscription-plans-grid">
                <div className="subscription-plan-card">
                  <div>
                    <div className="subscription-plan-name" style={{ color: tierInfo.pitchallo.color }}>
                      {tierInfo.pitchallo.name}
                    </div>
                    <div className="subscription-plan-price">
                      ${billingPeriod === 'monthly' ? PLAN.monthly.price : PLAN.yearly.price}
                      <span className="subscription-plan-interval">
                        {billingPeriod === 'monthly' ? '/month' : '/year'}
                      </span>
                    </div>

                    {/* Savings Badge */}
                    {billingPeriod === 'yearly' && PLAN.yearly.savings && (
                      <div className="subscription-savings-badge">
                        Save ${PLAN.yearly.savings.amount} ({PLAN.yearly.savings.months} months free)
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
                      Includes:
                    </div>
                    {[
                      'Unlimited pitches',
                      'Unlimited teams',
                      'Unlimited users',
                      'Advanced scheduling',
                      'Weather alerts',
                      'Cloud backup',
                      'Smart fixture tool',
                      'Change log'
                    ].map((feature, idx) => (
                      <div key={idx} style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        • {feature}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleUpgrade}
                    disabled={(loading && loadingPlan === 'pitchallo') || !isAdmin}
                    className="subscription-plan-button"
                    style={{
                      backgroundColor: isAdmin ? tierInfo.pitchallo.color : '#9ca3af',
                      opacity: (isAdmin && !(loading && loadingPlan === 'pitchallo')) ? 1 : 0.6
                    }}
                  >
                    {loadingPlan === 'pitchallo' && loading ? 'Processing...' : 'Subscribe'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Manage Subscription (for active/canceled) */}
          {(subscriptionStatus === 'active' || subscriptionStatus === 'canceled') && isAdmin && (
            <div className="subscription-manage-card">
              <h3>Manage Billing</h3>
              <p>
                Update payment methods, view invoices, or cancel your subscription
              </p>
              <button
                onClick={handleManageSubscription}
                disabled={loading}
                className="subscription-manage-button"
              >
                Manage Subscription
              </button>
            </div>
          )}

          {/* Admin Only Notice */}
          {!isAdmin && (
            <div className="subscription-admin-notice">
              ⚠️ Only administrators can manage subscription plans
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
